import { type FC, useState } from 'react'
import { useIdleTimer } from 'react-idle-timer'
import { useTimer } from 'react-timer-hook'
import cn from 'classnames'
// import { useAuth } from '@egw/auth'
import { useTranslation } from '@egw/i18n'
import { ModalDrawer, Button, ButtonVariant } from '@egw/components'
import { useNavigate } from 'react-router'
// import { WEALTH_ROUTES } from '@egw/constants'
import { configClient } from '@egw/config'
import { useAuth } from '@egw/auth'
import { type Config } from '../../config/config'

const IDLE_TIMEOUT = 30 * 60 * 1000 // 30 minutes
const TIMER_DURATION = 59 * 1000 // 59 seconds

// // Debug constants
// const IDLE_TIMEOUT = 3 * 1000
// const TIMER_DURATION = 59 * 1000

interface IdleModalProps {
  message: string
  hasSignOut: boolean
  signOutRoute: string
  onClose: () => void
  onSignOut: () => void
}

const IdleContent: FC<IdleModalProps> = ({ message, hasSignOut, signOutRoute, onClose, onSignOut }) => {
  const { t } = useTranslation('features-enrollment')
  const { signOut } = useAuth()
  const navigate = useNavigate()
  const { seconds } = useTimer({
    expiryTimestamp: new Date(Date.now() + TIMER_DURATION),
    autoStart: true,
    onExpire: async () => {
      onClose()
      void navigate(`${signOutRoute}?session-timeout=true`)
      await signOut()
    },
  })

  const config = configClient.getConfig() as Config

  return (
    <div className="flex flex-col gap-4">
      <span className="text-base">{message}</span>
      <div
        className={cn('justify-center items-center flex py-12 bg-bglight typo-primary-5xl-light', {
          'text-text-error': seconds <= 10,
        })}
      >
        00 : {seconds >= 0 ? <>{seconds < 10 ? `0${seconds.toString()}` : seconds}</> : '00'}
      </div>
      <Button
        id="sessionModalCta"
        onClick={() => {
          onClose()
        }}
      >
        {t('common.idle_unauthenticated.extend_time')}
      </Button>
      {hasSignOut ? (
        <Button
          id="signOutCta"
          variant={ButtonVariant.Secondary}
          onClick={() => {
            onSignOut()
            void signOut()
            window.location.href = config.globalBaseCompanyUrl
          }}
        >
          {t('common.idle_unauthenticated.sign_out')}
        </Button>
      ) : null}
    </div>
  )
}

export interface IdleTimerEnrollmentProps {
  title: string
  message: string
  hasSignOut?: boolean
  signOutRoute: string
  onSignOut: () => void
}

export const IdleTimerEnrollment: FC<IdleTimerEnrollmentProps> = ({
  title,
  message,
  signOutRoute,
  hasSignOut = true,
  onSignOut,
}) => {
  const [isOpen, setIsOpen] = useState(false)

  useIdleTimer({
    onIdle: () => {
      setIsOpen(true)
    },
    timeout: IDLE_TIMEOUT,
    throttle: 1000,
  })

  return (
    <ModalDrawer
      isOpen={isOpen}
      title={title}
      onClose={() => {
        setIsOpen(false)
      }}
    >
      {isOpen ? (
        <IdleContent
          message={message}
          hasSignOut={hasSignOut}
          signOutRoute={signOutRoute}
          onClose={() => {
            setIsOpen(false)
          }}
          onSignOut={onSignOut}
        />
      ) : null}
    </ModalDrawer>
  )
}
